/**
 * PoiMap — carte Leaflet des points d'intérêt (fond IGN Géoplateforme).
 * Wrapper léger autour de Leaflet (pas de react-leaflet, comme CollecteMap).
 *
 * - Marqueurs colorés par FAMILLE (via le type), popup au clic.
 * - Mode « pose » (onPick) : cliquer sur la carte remonte les coordonnées au
 *   parent (écran mairie qui crée/édite un point).
 * - S'ajuste à l'emprise des points ; France par défaut si vide.
 */

import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { PoiPoint } from '@/lib/api';
import { POI_FAMILLE_COLOR, POI_TYPE_EMOJI, POI_TYPE_LABEL, familleDuType } from './poiTypes';

const IGN_TILES = 'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2&STYLE=normal&TILEMATRIXSET=PM&FORMAT=image/png&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}';
const IGN_ATTRIB = '© IGN — Géoplateforme';

interface Props {
  points: PoiPoint[];
  height?: number;
  onPick?: (coords: { lat: number; lng: number }) => void;
  draft?: { lat: number; lng: number } | null;
}

function markerIcon(color: string, emoji: string): L.DivIcon {
  return L.divIcon({
    className: 'poi-pin',
    html: `<div style="width:26px;height:26px;transform:translate(-50%,-100%);">
      <div style="width:24px;height:24px;border-radius:50% 50% 50% 0;background:${color};
        border:2px solid rgba(255,255,255,.9);transform:rotate(-45deg);
        box-shadow:0 2px 5px rgba(0,0,0,.4);display:grid;place-items:center;">
        <span style="transform:rotate(45deg);font-size:12px;line-height:1;">${emoji}</span>
      </div></div>`,
    iconSize: [26, 26],
    iconAnchor: [0, 0],
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
}

export function PoiMap({ points, height = 340, onPick, draft }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const draftRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = L.map(ref.current, { attributionControl: true, zoomControl: true });
    L.tileLayer(IGN_TILES, { attribution: IGN_ATTRIB, maxZoom: 19 }).addTo(map);
    map.setView([46.6, 2.5], 5);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 60);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !onPick) return;
    const handler = (e: L.LeafletMouseEvent) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
    map.on('click', handler);
    return () => { map.off('click', handler); };
  }, [onPick]);

  useEffect(() => {
    const map = mapRef.current;
    const group = layerRef.current;
    if (!map || !group) return;
    group.clearLayers();

    const withCoords = points.filter((p) => p.latitude !== null && p.longitude !== null);
    for (const p of withCoords) {
      const fam = familleDuType(p.type);
      const color = POI_FAMILLE_COLOR[fam];
      const m = L.marker([p.latitude!, p.longitude!], {
        icon: markerIcon(color, POI_TYPE_EMOJI[p.type] ?? '📍'),
      });
      const horaires = p.horaires ? `<div style="font-size:11px;color:#9aa;">🕒 ${escapeHtml(p.horaires)}</div>` : '';
      const adresse = p.adresse ? `<div style="font-size:11px;color:#9aa;">${escapeHtml(p.adresse)}</div>` : '';
      const desc = p.description ? `<div style="margin-top:5px;font-size:11px;color:#9aa;">${escapeHtml(p.description)}</div>` : '';
      m.bindPopup(
        `<div style="min-width:150px;">
          <div style="font-weight:600;font-size:13px;">${escapeHtml(p.nom)}</div>
          <div style="font-size:10px;text-transform:uppercase;letter-spacing:.04em;color:${color};">${escapeHtml(POI_TYPE_LABEL[p.type] ?? p.type)}</div>
          ${adresse}${horaires}${desc}
        </div>`,
        { closeButton: true }
      );
      m.addTo(group);
    }

    if (withCoords.length > 0) {
      try {
        const bounds = L.latLngBounds(withCoords.map((p) => [p.latitude!, p.longitude!] as [number, number]));
        map.fitBounds(bounds, { padding: [30, 30], maxZoom: 16 });
      } catch { /* vue France conservée */ }
    }
    setTimeout(() => map.invalidateSize(), 60);
  }, [points]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (draftRef.current) { map.removeLayer(draftRef.current); draftRef.current = null; }
    if (draft) {
      const m = L.marker([draft.lat, draft.lng], { icon: markerIcon('#1d9e88', '✚') });
      m.addTo(map);
      draftRef.current = m;
      map.setView([draft.lat, draft.lng], Math.max(map.getZoom(), 15));
    }
  }, [draft]);

  return (
    <div ref={ref} style={{ height, borderRadius: 11, overflow: 'hidden' }} className="border border-border" />
  );
}
